import React from 'react';
import { tv } from 'tailwind-variants';
import { twMerge } from '@/utils';
import Icon from '@/components/ui/Icon';

interface DropdownButtonProps {
  checkmarkPlacement?: 'left' | 'right' | 'none';
  children: ReactChildren;
  className?: string;
  disabled?: boolean;
  href?: string;
  onClick?: () => void;
  selected?: boolean;
  title?: string;
  type?: 'button' | 'link';
}

const buttonVariants = tv({
  base: [
    'tw-cursor-pointer reading-level-choice dropdown-item tw-py-2 -tw-outline-offset-1',
  ],
  variants: {
    checkmarkPlacement: {
      left: 'tw-flex tw-items-center',
      none: '',
      right: 'tw-flex tw-items-center',
    },
    disabled: {
      true: '!tw-cursor-not-allowed !tw-pointer-events-auto tw-text-grey',
    },
    type: {
      button: 'tw-w-full',
      link: 'tw-w-auto',
    },
  },
});

const DropdownButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  DropdownButtonProps
>(({
  checkmarkPlacement = 'none',
  children,
  className = '',
  disabled = false,
  href,
  onClick = () => {},
  selected = false,
  title,
  type = 'button',
}, ref) => {
  const ButtonComponent = type === 'link' ? 'a' : 'button';

  return (
    <ButtonComponent
      ref={ref as any}
      className={twMerge(
        buttonVariants({ checkmarkPlacement, disabled, type }),
        className,
      )}
      disabled={disabled}
      href={href}
      title={title}
      onClick={onClick}
    >
      {checkmarkPlacement === 'left' && (
        <span className="tw-inline-block tw-w-5">
          {selected && (
            <i className="fa fa-check tw-text-green" />
          )}
        </span>
      )}
      <span>{children}</span>
      {checkmarkPlacement === 'right' && (
        <div className="tw-flex tw-items-center tw-justify-center tw-ml-auto tw-w-5">
          {selected && (
            <Icon fill="#2A8806" icon="Checkmark" size={24} />
          )}
        </div>
      )}
    </ButtonComponent>
  );
});

DropdownButton.displayName = 'Dropdown.Button';

export default DropdownButton;
